import React, { useEffect, useState } from 'react';
import { NavBar } from '../kuroco/AppBars';
import { fmt, EM } from '../kuroco/fmt';
import {
  saveProfileLocal,
  saveNotifLocal,
  clearLocalAppData,
  notifyGoalsUpdated,
} from '../../utils/goalsLocalStore';
import { resetGoals } from '../../api/goalsApi';
import { changeMyHomeStore, fetchMyIdentity, saveMyName } from '../../api/authApi';
import { resolveCallName, resolveCallSuffix } from '../../utils/callName';
import { useDashboard } from '../../context/DashboardContext';
import { nextFocusSlot } from '../../utils/kpiFocusCatalog';
import { useSettingsData } from './useSettingsData';
import { useNavigateWithFilters } from '../../utils/dashboardNav';
import { useUserRole } from '../../hooks/useUserRole';
import { downloadCsv, isDebugEnabled, openDebugPanel } from '../../debug/debugLogger';
import { useUnseenAnnouncements } from '../../hooks/useAnnouncements';
import { useInquiryBadges } from '../../hooks/useInquiryBadges';
import { MANUAL_LINKS } from '../../constants/manualLinks';
import { invalidateAnnouncementsSummary } from '../../api/announcementsApi';
import {
  disablePush,
  enablePush,
  fetchPushSettings,
  forgetThisDevice,
  pushSupport,
  saveReminder,
  sendBroadcast,
  sendTestPush,
} from '../../push/pushClient';

function Toggle({ on, set }) {
  return (
    <button type="button" className={`set-toggle${on ? ' is-on' : ''}`} onClick={() => set(!on)} aria-label="切替">
      <span className="set-toggle-knob" />
    </button>
  );
}

export default function StylistSettingsScreen() {
  const { role, canAccessIam, isDev } = useUserRole();
  const unseenAnnouncements = useUnseenAnnouncements();
  const inquiryBadges = useInquiryBadges(isDev);
  const navigate = useNavigateWithFilters();
  const {
    user, logout, D, profileLocal, setProfileLocal, notif, setNotif,
    selectedStore, selectedStylist, goals,
  } = useSettingsData();
  // 目標・注力の変更をマネージャーが確認中（値はもう反映されている）
  const goalsPending = goals?.goalsApprovalStatus === 'pending';

  const gt = D.goalTrack;
  const addFocusSlot = nextFocusSlot(D.focusSlots.map((f) => f.slot));
  const yen = (v) => (v != null && v !== EM ? fmt('currency', v) : EM);
  const daysToGoal =
    gt.goalDate != null
      ? Math.max(0, Math.round((new Date(gt.goalDate) - new Date(D.period.today)) / 86400000))
      : null;

  const [editing, setEditing] = useState(false);
  const [pName, setPName] = useState(D.stylist.name);
  const [pRank, setPRank] = useState(D.stylist.rank === EM ? '' : D.stylist.rank);
  const [pDebutY, setPDebutY] = useState(D.stylist.debutYear || new Date().getFullYear() - 3);
  const [pDebutM, setPDebutM] = useState(D.stylist.debutMonth || 4);

  // 所属店舗 lives on the account (users.home_store), not in this browser. The old
  // 店舗 field of プロフィールを編集 (localStorage only, read nowhere else) is gone.
  const { myProfile, setMyProfile, stores: myStores, setSelectedStore } = useDashboard();
  const homeStore = myProfile?.stylist_name_pos ? myProfile.home_store : null;
  const [storeOpen, setStoreOpen] = useState(false);
  const [storePick, setStorePick] = useState('');
  const [storeBusy, setStoreBusy] = useState(false);
  const [storeError, setStoreError] = useState('');

  const openStorePicker = () => {
    setStorePick(homeStore || '');
    setStoreError('');
    setStoreOpen(true);
  };

  const saveHomeStore = async () => {
    if (!storePick || storePick === homeStore || storeBusy) return;
    setStoreBusy(true);
    setStoreError('');
    try {
      const next = await changeMyHomeStore(storePick);
      setMyProfile(next);
      setSelectedStore(storePick); // own record is picked again from the new store's list
      setStoreOpen(false);
    } catch (err) {
      setStoreError(err?.message || '所属店舗を変更できませんでした');
    } finally {
      setStoreBusy(false);
    }
  };

  // 氏 / 名 live on the account (users doc); the AI calls the person by 名. dev / no profile → old local name field.
  const canSaveName = Boolean(myProfile) && role !== 'dev';

  // The profile is loaded once at login; the AI chat can change 名 since then. Re-read it when 設定 opens,
  // or the form shows the old (empty) 名 while the chat already uses the new one (渡邊 demo, 2026-10-26).
  const hasProfile = Boolean(myProfile);
  useEffect(() => {
    if (!hasProfile || role === 'dev') return undefined;
    let cancelled = false;
    fetchMyIdentity()
      .then((p) => {
        if (!cancelled && p && typeof p === 'object') setMyProfile(p);
      })
      .catch(() => {}); // keep what we have
    return () => {
      cancelled = true;
    };
  }, [hasProfile, role, setMyProfile]);
  const accountName = [myProfile?.family_name, myProfile?.given_name].filter(Boolean).join(' ');
  const [pFamily, setPFamily] = useState('');
  const [pGiven, setPGiven] = useState('');
  const [nameBusy, setNameBusy] = useState(false);
  const [nameError, setNameError] = useState('');

  const profile = {
    name: accountName || profileLocal?.name || (D.stylist.name !== EM ? D.stylist.name : ''),
    rank: profileLocal?.rank || (D.stylist.rank !== EM ? D.stylist.rank : ''),
    debutY: profileLocal?.debutY ?? D.stylist.debutYear ?? new Date().getFullYear() - 3,
    debutM: profileLocal?.debutM ?? D.stylist.debutMonth ?? 4,
  };

  const NOW_Y = new Date(D.period.today).getFullYear();
  const DEBUT_YEARS = Array.from({ length: NOW_Y - 2004 }, (_, i) => NOW_Y - i);
  const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);
  const debutYears = Math.max(0, NOW_Y - profile.debutY);

  const startEdit = () => {
    setPName(profile.name);
    setPFamily(myProfile?.family_name || '');
    setPGiven(myProfile?.given_name || '');
    setNameError('');
    setPRank(profile.rank);
    setPDebutY(profile.debutY);
    setPDebutM(profile.debutM);
    setEditing(true);
  };

  const saveEdit = async () => {
    if (nameBusy) return;
    if (canSaveName) {
      const family = pFamily.trim();
      const given = pGiven.trim();
      if (family !== (myProfile?.family_name || '') || given !== (myProfile?.given_name || '')) {
        setNameBusy(true);
        setNameError('');
        try {
          setMyProfile(await saveMyName({ familyName: family, givenName: given }));
        } catch (err) {
          setNameError(err?.message || '氏名を保存できませんでした');
          setNameBusy(false);
          return; // keep the form open; nothing else saved
        }
        setNameBusy(false);
      }
    }
    const next = {
      name: canSaveName ? profileLocal?.name ?? '' : pName.trim() || profile.name,
      rank: pRank.trim(),
      debutY: pDebutY,
      debutM: pDebutM,
    };
    saveProfileLocal(user?.uid, next);
    setProfileLocal(next);
    setEditing(false);
  };

  const updateNotif = (patch) => {
    const next = { ...notif, ...patch };
    saveNotifLocal(user?.uid, next);
    setNotif(next);
  };

  // ── プッシュ通知 / 毎日のリマインド ──
  // Two switches on purpose: 「プッシュ通知」 registers this phone (確認依頼 pushes for managers,
  // event-based); 「毎日のリマインド」 is the time-based reminder on top of it. With one switch for
  // both, a manager with the reminder off silently lost 確認依頼 pushes.
  // Both show the real state (server + this device's permission), never a local default.
  const [pushOn, setPushOn] = useState(false);
  const [pushNote, setPushNote] = useState(null);
  const [pushBusy, setPushBusy] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const support = await pushSupport();
      if (!support.ok) {
        if (!cancelled) { setPushNote(support.reason || null); setPushOn(false); updateNotif({ enabled: false }); }
        return;
      }
      try {
        const s = await fetchPushSettings();
        const granted = typeof Notification !== 'undefined' && Notification.permission === 'granted';
        const on = Boolean(granted && s.devices > 0);
        if (!cancelled) {
          setPushOn(on);
          updateNotif({ enabled: Boolean(on && s.enabled), time: s.time || notif.time });
        }
      } catch {
        // offline / not signed in — keep what's on screen
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per user
  }, [user?.uid]);

  const togglePush = async (on) => {
    if (pushBusy) return;
    setPushBusy(true);
    setPushNote(null);
    try {
      if (on) {
        // Permission prompt: must stay inside this tap. Turning push on also turns the daily
        // reminder on (the common case); it can be switched off separately below.
        await enablePush({ reminder: true, time: notif.time });
        setPushOn(true);
        updateNotif({ enabled: true });
        setPushNote('プッシュ通知をオンにしました。「テスト通知を送る」で届くか確認できます。');
      } else {
        await disablePush(notif.time);
        setPushOn(false);
        updateNotif({ enabled: false });
      }
    } catch (err) {
      setPushNote(err?.message || '通知の設定に失敗しました');
    } finally {
      setPushBusy(false);
    }
  };

  const toggleReminder = async (on) => {
    const prev = notif.enabled;
    updateNotif({ enabled: on });
    try {
      await saveReminder({ enabled: on, time: notif.time });
    } catch (err) {
      updateNotif({ enabled: prev });
      setPushNote(err?.message || 'リマインドの設定を保存できませんでした');
    }
  };

  const changePushTime = async (time) => {
    updateNotif({ time });
    try {
      await saveReminder({ enabled: true, time });
    } catch (err) {
      setPushNote(err?.message || '通知時間を保存できませんでした');
    }
  };

  const testPush = async () => {
    setPushNote(null);
    try {
      const r = await sendTestPush();
      setPushNote(`テスト通知を送信しました（${r.sent}台）。数秒以内に届きます。`);
    } catch (err) {
      setPushNote(err?.message || 'テスト通知を送れませんでした');
    }
  };

  // ── お知らせの一斉送信（dev だけ。決定 2026-10-13） ──
  const [announce, setAnnounce] = useState({ title: '', body: '' });
  const [announceNote, setAnnounceNote] = useState(null);
  const [announceBusy, setAnnounceBusy] = useState(false);
  const sendAnnounce = async () => {
    if (announceBusy || !announce.title.trim() || !announce.body.trim()) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`全ユーザーの端末に送ります。よろしいですか？\n\n${announce.title}\n${announce.body}`)) return;
    setAnnounceBusy(true);
    setAnnounceNote(null);
    try {
      const r = await sendBroadcast({ title: announce.title.trim(), body: announce.body.trim() });
      setAnnounceNote(`送信しました（${r.sent}台、失敗 ${r.failed}）。設定 → お知らせ に残ります`);
      invalidateAnnouncementsSummary();
      setAnnounce({ title: '', body: '' });
    } catch (err) {
      setAnnounceNote(err?.message || '送信に失敗しました');
    } finally {
      setAnnounceBusy(false);
    }
  };

  const handleLogout = async () => {
    await forgetThisDevice(); // a signed-out phone must not keep getting this person's reminders
    await logout();
    navigate('/login');
  };

  // ── 開発用リセット ──
  // Two-step on purpose: this throws away the stylist's goals, and the numbers
  // it clears are the same ones the charts and the AI read.
  const [resetArmed, setResetArmed] = useState(false);
  const [resetState, setResetState] = useState('idle');
  const [resetReport, setResetReport] = useState(null);
  const canResetRemote = Boolean(selectedStore && selectedStylist);

  const handleReset = async () => {
    if (!resetArmed) {
      setResetArmed(true);
      setResetReport(null);
      return;
    }
    const scope = canResetRemote
      ? `${selectedStore} / ${selectedStylist} の目標・注力指標・行動プラン`
      : 'この端末のローカルデータのみ（店舗・スタイリスト未選択のためサーバー側は消せません）';
    if (!window.confirm(`次を消去します。元に戻せません。\n\n${scope}\n端末の設定キャッシュ（表示順・フィルタ・プロフィール・通知）`)) {
      setResetArmed(false);
      return;
    }

    setResetState('running');
    const cleared = clearLocalAppData();
    let remote = null;
    let failure = null;
    if (canResetRemote) {
      try {
        const res = await resetGoals({ store: selectedStore, stylist: selectedStylist });
        remote = res?.goals ?? null;
        notifyGoalsUpdated(remote);
      } catch (err) {
        failure = err.message || '目標のリセットに失敗しました';
      }
    }

    setResetArmed(false);
    setResetState(failure ? 'error' : 'done');
    setResetReport({
      cleared: cleared.length,
      remote: canResetRemote ? (failure ? 'failed' : 'cleared') : 'skipped',
      failure,
    });
    // Every hook holds its own copy of this state — only a reload guarantees
    // the screens agree with what was just wiped.
    if (!failure) setTimeout(() => window.location.reload(), 900);
  };

  return (
    <div className="app">
      <NavBar title="設定" backLabel="ホーム" backTo="/" hideSettings />
      <div className="scroll fade">
        {!editing ? (
          <div className="set-profile">
            <span className="set-avatar">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="8" r="4" stroke="#fff" strokeWidth="2" />
                <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </span>
            <div className="set-profile-body">
              <div className="set-profile-name">{profile.name || EM}</div>
              <div className="set-profile-sub">
                {profile.rank || EM} · {homeStore || selectedStore || EM}
              </div>
              <div className="set-profile-sub">
                デビュー {profile.debutY}年{profile.debutM}月（{debutYears}年目）
              </div>
            </div>
            <button type="button" className="set-edit" onClick={startEdit}>
              編集
            </button>
          </div>
        ) : (
          <div className="set-profile set-profile--edit">
            <div className="pedit-head">
              <span className="set-avatar pedit-avatar">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="8" r="4" stroke="#fff" strokeWidth="2" />
                  <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </span>
              <div className="pedit-headt">プロフィールを編集</div>
            </div>
            {canSaveName ? (
              <div className="pedit-field">
                <span className="pedit-label">氏名</span>
                <div className="pedit-ym">
                  <input
                    className="pedit-input"
                    value={pFamily}
                    onChange={(e) => setPFamily(e.target.value)}
                    placeholder="氏"
                    aria-label="氏"
                    maxLength={20}
                    disabled={nameBusy}
                  />
                  <input
                    className="pedit-input"
                    value={pGiven}
                    onChange={(e) => setPGiven(e.target.value)}
                    placeholder="名"
                    aria-label="名"
                    maxLength={20}
                    disabled={nameBusy}
                  />
                </div>
                {nameError && <div className="set-note" role="alert">{nameError}</div>}
                <div className="set-note">
                  AIの呼び方: 「{resolveCallName(myProfile, myProfile?.stylist_name_pos)}{resolveCallSuffix(myProfile)}」。
                  AI相談で「〇〇って呼んで」と言うと変えられます（ここの氏名にも反映）
                </div>
              </div>
            ) : (
              <label className="pedit-field">
                <span className="pedit-label">名前</span>
                <input className="pedit-input" value={pName} onChange={(e) => setPName(e.target.value)} placeholder="氏名" />
              </label>
            )}
            <label className="pedit-field">
              <span className="pedit-label">ランク</span>
              <input className="pedit-input" value={pRank} onChange={(e) => setPRank(e.target.value)} placeholder="スタイリスト" />
            </label>
            <div className="pedit-field">
              <span className="pedit-label">スタイリストデビュー年月</span>
              <div className="pedit-ym">
                <select className="pedit-select" value={pDebutY} onChange={(e) => setPDebutY(+e.target.value)}>
                  {DEBUT_YEARS.map((y) => (
                    <option key={y} value={y}>
                      {y}年
                    </option>
                  ))}
                </select>
                <select className="pedit-select" value={pDebutM} onChange={(e) => setPDebutM(+e.target.value)}>
                  {MONTHS.map((m) => (
                    <option key={m} value={m}>
                      {m}月
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="pedit-actions">
              <button type="button" className="pedit-cancel" onClick={() => setEditing(false)}>
                キャンセル
              </button>
              <button type="button" className="pedit-save" onClick={saveEdit} disabled={nameBusy}>
                {nameBusy ? '保存中…' : '保存'}
              </button>
            </div>
          </div>
        )}

        {homeStore && (
          <div className="set-group">
            <div className="set-group-head">
              <span className="set-group-title">所属</span>
            </div>
            <div className="set-card">
              <button
                type="button"
                className="set-row set-card--tap"
                style={{ width: '100%', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer' }}
                onClick={openStorePicker}
                aria-expanded={storeOpen}
              >
                <span className="set-ic">🏠</span>
                <div className="set-row-body">
                  <div className="set-row-t">所属店舗</div>
                  <div className="set-row-d">ホームを開くと最初に表示されます</div>
                </div>
                <span className="set-row-val accent">{homeStore}</span>
              </button>
              {storeOpen && (
                <div className="set-profile--edit" style={{ padding: '4px 14px 14px' }}>
                  <label className="pedit-field">
                    <span className="pedit-label">新しい所属店舗</span>
                    <select
                      id="home-store-select"
                      className="pedit-select"
                      value={storePick}
                      disabled={storeBusy}
                      onChange={(e) => setStorePick(e.target.value)}
                    >
                      {(myStores || []).map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="set-note">
                    目標・行動プラン・行動記録はそのまま引き継がれます。確認中の行動は新しい店舗の管理画面に移ります。売上などの実績は全店舗の合計のまま変わりません。
                  </div>
                  {storeError && (
                    <div className="set-note" role="alert">
                      {storeError}
                    </div>
                  )}
                  <div className="pedit-actions">
                    <button type="button" className="pedit-cancel" disabled={storeBusy} onClick={() => setStoreOpen(false)}>
                      キャンセル
                    </button>
                    <button
                      type="button"
                      className="pedit-save"
                      disabled={storeBusy || !storePick || storePick === homeStore}
                      onClick={saveHomeStore}
                    >
                      {storeBusy ? '変更中…' : '変更する'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="set-group">
          <div className="set-group-head">
            <span className="set-group-title">目標設定</span>
            <button type="button" className="set-group-edit" onClick={() => navigate('/settings/goaledit')}>
              編集
            </button>
          </div>
          <div className="set-card">
            <button
              type="button"
              className="set-row set-card--tap"
              style={{ width: '100%', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer' }}
              onClick={() => navigate('/settings/goaledit?edit=goal')}
            >
              <span className="set-ic">🎯</span>
              <div className="set-row-body">
                <div className="set-row-t">
                  最終ゴール（KGI ①）
                  {goalsPending && <span className="plan-badge plan-badge--pending">確認中</span>}
                </div>
                <div className="set-row-d">単月売上 {yen(gt.finalSales)}</div>
              </div>
              <span className="set-row-val accent">
                {daysToGoal != null ? `残り${daysToGoal}日` : EM}
              </span>
            </button>
            {/* KGI ② has its own row and its own edit screen (?edit=subkgi) since 2026-09-30 */}
            <button
              type="button"
              className="set-row set-card--tap"
              style={{ width: '100%', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer' }}
              onClick={() => navigate('/settings/goaledit?edit=subkgi')}
            >
              <span className="set-ic">{D.subKgi?.icon || '🎯'}</span>
              <div className="set-row-body">
                <div className="set-row-t">追加KGI（KGI ②）</div>
                <div className="set-row-d">{D.subKgi ? D.subKgi.label : 'なし（任意）'}</div>
              </div>
              <span className="set-row-val accent">{D.subKgi ? `目標 ${D.subKgi.target}` : '追加'}</span>
            </button>
            <button
              type="button"
              className="set-row set-card--tap"
              style={{ width: '100%', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer' }}
              onClick={() => navigate('/settings/goaledit?edit=period')}
            >
              <span className="set-ic">🗓</span>
              <div className="set-row-body">
                <div className="set-row-t">目標期間</div>
                <div className="set-row-d">
                  {gt.goalDateLabel ? `〜${gt.goalDateLabel}（${gt.planLength ?? 6}ヶ月）` : `${gt.planLength ?? 6}ヶ月プラン`}
                </div>
              </div>
              <span className="set-row-val">{gt.phaseLabel}</span>
            </button>
            {/* 注力 — slots fill in order (① → ② → ③, no gaps). Each set slot opens its editor (where
                ②③ can also be removed); one「追加」row opens the first empty slot (nextFocusSlot). */}
            {[D.focus, D.subFocus, D.thirdFocus].filter((f) => f.isSelected).map((f) => (
              <button
                key={f.slot}
                type="button"
                className="set-row set-card--tap"
                style={{ width: '100%', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer' }}
                onClick={() => navigate(`/settings/goaledit?edit=kpi${f.slot}`)}
              >
                <span className="set-ic">{f.icon}</span>
                <div className="set-row-body">
                  <div className="set-row-t">注力指標 {['①', '②', '③'][f.slot - 1]}</div>
                  <div className="set-row-d">{f.label}</div>
                </div>
                <span className="set-row-val accent">目標 {f.target}</span>
              </button>
            ))}
            {addFocusSlot && (
              <button
                type="button"
                className="set-row set-card--tap"
                style={{ width: '100%', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer' }}
                onClick={() => navigate(`/settings/goaledit?edit=kpi${addFocusSlot}`)}
              >
                <span className="set-ic">➕</span>
                <div className="set-row-body">
                  <div className="set-row-t">注力指標を追加</div>
                  <div className="set-row-d">
                    {addFocusSlot === 1 ? '未選択（1つは必要です）' : `あと${4 - addFocusSlot}つまで（任意）`}
                  </div>
                </div>
                <span className="set-row-val accent">{addFocusSlot === 1 ? '選ぶ' : '追加'}</span>
              </button>
            )}
          </div>
          <div className="set-note">登録時に設定した目標です。変更すると予測とロードマップに反映されます。</div>
        </div>

        <div className="set-group">
          <div className="set-group-head">
            <span className="set-group-title">毎日の行動プラン</span>
            <button type="button" className="set-group-edit" onClick={() => navigate('/settings/actionedit')}>
              編集
            </button>
          </div>
          <button type="button" className="set-card set-card--tap" onClick={() => navigate('/settings/actionedit')}>
            {D.actions.length === 0 ? (
              <div className="set-row">
                <span className="set-ic">✅</span>
                <div className="set-row-body">
                  <div className="set-row-t">未設定</div>
                  <div className="set-row-d">目標設定の編集から行動プランを決定できます</div>
                </div>
              </div>
            ) : (
              D.actions.map((a) => (
                <div className="set-row" key={a.id}>
                  <span className="set-num">{a.id}</span>
                  <div className="set-row-body">
                    <div className="set-row-t">{a.name}</div>
                    <div className="set-row-d">{a.cadence}</div>
                  </div>
                  <svg className="set-chev" viewBox="0 0 8 14" fill="none" aria-hidden="true">
                    <path d="M1 1l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              ))
            )}
          </button>
          <div className="set-note">
            行動プランは「目標設定」の編集画面で、注力指標と一緒にAIマネージャーと相談して決定します。
          </div>
          <button type="button" className="set-history-link" onClick={() => navigate('/settings/history')}>
            <span className="set-history-ic">🕑</span>
            <div className="set-history-body">
              <div className="set-history-t">変更履歴を見る</div>
              <div className="set-history-d">目標・注力指標・行動プランの変更ログ</div>
            </div>
            <svg className="set-chev" viewBox="0 0 8 14" fill="none" aria-hidden="true">
              <path d="M1 1l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        <div className="set-group">
          <div className="set-group-head">
            <span className="set-group-title">アプリ設定</span>
          </div>
          <div className="set-card">
            <div className="set-row">
              <span className="set-ic set-ic--plain">🔔</span>
              <div className="set-row-body">
                <div className="set-row-t">プッシュ通知</div>
                <div className="set-row-d">
                  {pushBusy
                    ? '設定中…'
                    : pushOn
                      ? canAccessIam
                        ? 'この端末で受け取る（確認依頼・リマインド）'
                        : 'この端末で受け取る'
                      : 'オフ'}
                </div>
              </div>
              <Toggle on={pushOn} set={(v) => togglePush(v)} />
            </div>
            {pushNote && <div className="set-note" role="status">{pushNote}</div>}
            {pushOn && (
              <div className="set-row">
                <span className="set-ic set-ic--plain">⏰</span>
                <div className="set-row-body">
                  <div className="set-row-t">毎日のリマインド</div>
                  <div className="set-row-d">{notif.enabled ? '未記録の日だけ、通知時間に行動記録を通知' : 'オフ'}</div>
                </div>
                <Toggle on={notif.enabled} set={(v) => toggleReminder(v)} />
              </div>
            )}
            {pushOn && notif.enabled && (
              <div className="set-row">
                <span className="set-ic set-ic--plain">🕗</span>
                <div className="set-row-body">
                  <div className="set-row-t">通知時間</div>
                  <div className="set-row-d">行動記録のリマインドを送る時刻</div>
                </div>
                <label className="set-timewrap">
                  <span className="set-row-val accent">{notif.time}</span>
                  <input
                    type="time"
                    className="set-time"
                    value={notif.time}
                    step="300"
                    onChange={(e) => changePushTime(e.target.value || notif.time)}
                  />
                </label>
              </div>
            )}
            {pushOn && (
              <button type="button" className="set-row set-card--tap" onClick={testPush}>
                <span className="set-ic set-ic--plain">📨</span>
                <div className="set-row-body">
                  <div className="set-row-t">テスト通知を送る</div>
                  <div className="set-row-d">この端末に通知が届くか確認します</div>
                </div>
              </button>
            )}
          </div>
        </div>

        <div className="set-group">
          <div className="set-group-head">
            <span className="set-group-title">アカウント</span>
          </div>
          <div className="set-card">
            <button type="button" className="set-row" style={{ width: '100%', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer' }} onClick={() => navigate('/settings/updates')}>
              <span className="set-ic set-ic--plain">📣</span>
              <div className="set-row-body">
                <div className="set-row-t">お知らせ</div>
                <div className="set-row-d">KUROCO からのお知らせ（90 日分）</div>
              </div>
              {unseenAnnouncements > 0 && <span className="set-badge">新着 {unseenAnnouncements}</span>}
              <svg className="set-chev" viewBox="0 0 8 14" fill="none" aria-hidden="true">
                <path d="M1 1l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button type="button" className="set-row" style={{ width: '100%', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer' }} onClick={() => navigate('/settings/help')}>
              <span className="set-ic set-ic--plain">❓</span>
              <div className="set-row-body">
                <div className="set-row-t">ヘルプ・お問い合わせ</div>
              </div>
              {inquiryBadges.unread > 0 && <span className="set-badge">返信あり {inquiryBadges.unread}</span>}
              <svg className="set-chev" viewBox="0 0 8 14" fill="none" aria-hidden="true">
                <path d="M1 1l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {isDev && (
              <button type="button" className="set-row" style={{ width: '100%', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer' }} onClick={() => navigate('/settings/help/dev')}>
                <span className="set-ic set-ic--plain">📬</span>
                <div className="set-row-body">
                  <div className="set-row-t">お問い合わせ管理</div>
                  <div className="set-row-d">開発者用: 全員分の一覧と返信</div>
                </div>
                {inquiryBadges.needsReply > 0 && <span className="set-badge">返信待ち {inquiryBadges.needsReply}</span>}
                <svg className="set-chev" viewBox="0 0 8 14" fill="none" aria-hidden="true">
                  <path d="M1 1l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* マニュアル — 外部の pdf（全ロール）。UI 案 2026-10-26 */}
        <div className="set-group">
          <div className="set-group-head">
            <span className="set-group-title">マニュアル</span>
          </div>
          <div className="set-card">
            {MANUAL_LINKS.map((m) => (
              <a
                key={m.key}
                className="set-row set-row--link"
                href={m.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="set-ic set-ic--manual" aria-hidden="true">
                  {m.icon === 'book' ? (
                    <svg viewBox="0 0 20 20" fill="none" width="18" height="18">
                      <path d="M10 5.5c-1.4-1.2-3.4-1.5-6-1.3v10.6c2.6-.2 4.6.1 6 1.3 1.4-1.2 3.4-1.5 6-1.3V4.2c-2.6-.2-4.6.1-6 1.3Zm0 0v10.6" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 20 20" fill="none" width="18" height="18">
                      <rect x="4" y="3" width="12" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
                      <path d="m7.5 10 1.8 1.8L12.8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                <div className="set-row-body">
                  <div className="set-row-t">{m.title}</div>
                  <div className="set-row-d">{m.desc}</div>
                </div>
                <svg className="set-ext" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path d="M6 2H2.5A.5.5 0 0 0 2 2.5v9a.5.5 0 0 0 .5.5h9a.5.5 0 0 0 .5-.5V8M8.5 2H12v3.5M12 2 6.5 7.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            ))}
          </div>
          <div className="set-note">※ 外部サイト（ブラウザ）で開きます</div>
          <button type="button" className="set-logout" onClick={handleLogout}>
            ログアウト
          </button>
        </div>

        {canAccessIam && (
          <div className="set-group">
            <div className="set-group-head">
              <span className="set-group-title">IAM · ユーザー管理</span>
            </div>
            <div className="set-card">
              <button type="button" className="set-row set-card--tap" onClick={() => navigate('/admin/iam')}>
                <span className="set-ic">👥</span>
                <div className="set-row-body">
                  <div className="set-row-t">招待・ユーザー一覧</div>
                  <div className="set-row-d">
                    {role === 'dev' || role === 'company_admin'
                      ? 'manager / beauty_manager / stylist を招待・企業一覧'
                      : 'あなたの業態のスタイリストを招待'}
                  </div>
                </div>
                <svg className="set-chev" viewBox="0 0 8 14" fill="none" aria-hidden="true">
                  <path d="M1 1l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
        )}

        <div className="set-group">
          <div className="set-group-head">
            <span className="set-group-title">開発用</span>
          </div>
          {role === 'dev' && (
            <div className="set-card" style={{ marginBottom: 10 }}>
              <div className="set-row">
                <span className="set-ic set-ic--plain">📣</span>
                <div className="set-row-body">
                  <div className="set-row-t">お知らせをプッシュ通知で送る</div>
                  <div className="set-row-d">全ユーザーの端末に届きます。タップ先は 設定 → お知らせ</div>
                </div>
              </div>
              <div className="set-bc-form">
                <input
                  type="text"
                  className="set-bc-input"
                  placeholder="タイトル（60字まで）"
                  maxLength={60}
                  value={announce.title}
                  onChange={(e) => setAnnounce({ ...announce, title: e.target.value })}
                />
                <textarea
                  className="set-bc-input"
                  placeholder="本文（200字まで）"
                  maxLength={200}
                  rows={3}
                  value={announce.body}
                  onChange={(e) => setAnnounce({ ...announce, body: e.target.value })}
                />
                <button
                  type="button"
                  className="quick-btn quick-btn--primary"
                  disabled={announceBusy || !announce.title.trim() || !announce.body.trim()}
                  onClick={sendAnnounce}
                >
                  {announceBusy ? '送信中…' : '全員に送る'}
                </button>
                {announceNote && <div className="set-note">{announceNote}</div>}
              </div>
            </div>
          )}
          {isDebugEnabled() && (
            <div className="set-card" style={{ marginBottom: 10 }}>
              <div className="set-row">
                <span className="set-ic set-ic--plain">🧪</span>
                <div className="set-row-body">
                  <div className="set-row-t">開発ログ（admin-dev）</div>
                  <div className="set-row-d">API / ナビ操作を Debug Panel で記録・CSV 出力</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, padding: '0 12px 12px' }}>
                <button type="button" className="quick-btn quick-btn--primary" onClick={() => openDebugPanel()}>
                  Debug Panel を開く
                </button>
                <button type="button" className="quick-btn quick-btn--ghost" onClick={() => downloadCsv()}>
                  CSV をエクスポート
                </button>
              </div>
            </div>
          )}
          <div className="set-card">
            <div className="set-row">
              <span className="set-ic set-ic--plain">🧨</span>
              <div className="set-row-body">
                <div className="set-row-t">すべてのデータをリセット</div>
                <div className="set-row-d">
                  {canResetRemote
                    ? `${selectedStore} / ${selectedStylist} の目標・注力指標・行動プラン＋端末のキャッシュ`
                    : '端末のキャッシュのみ（店舗・スタイリストを選ぶとサーバー側も消せます）'}
                </div>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="set-logout set-reset"
            onClick={handleReset}
            disabled={resetState === 'running'}
          >
            {resetState === 'running'
              ? 'リセット中…'
              : resetArmed
                ? '本当にリセットする（元に戻せません）'
                : 'すべてのデータをリセット'}
          </button>
          {resetArmed && resetState !== 'running' && (
            <button type="button" className="set-reset-cancel" onClick={() => setResetArmed(false)}>
              キャンセル
            </button>
          )}
          {resetReport && (
            <div className="set-note">
              {resetReport.failure
                ? `⚠ ローカル ${resetReport.cleared}件を消去。サーバー側は失敗: ${resetReport.failure}`
                : resetReport.remote === 'skipped'
                  ? `✅ ローカル ${resetReport.cleared}件を消去（サーバー側はスキップ）`
                  : `✅ ローカル ${resetReport.cleared}件とサーバーの目標を消去。再読み込みします…`}
            </div>
          )}
          <div className="set-ver">KUROCO for Stylist · v4.3</div>
        </div>
      </div>
    </div>
  );
}
